/**
 * The capture bridge, shared by the header control, the Refresh page and the two
 * pages a run writes to.
 *
 * In a context rather than per-component, because the PORT has to outlive a
 * navigation: a run is minutes long, and reconnecting on every route change
 * would both lose the run's pushed snapshots and wake the service worker over
 * and over.
 *
 * Context and hook live here, apart from the provider component, so this module
 * has no JSX and the pages that only read state do not import a component to get
 * it.
 */

import { createContext, useContext } from "react";
import type { FailureCode } from "../../../shared/capturePort";
import type { BridgeState } from "./captureBridge";

export interface CaptureValue extends BridgeState {
  /**
   * Bumped once each time a run finishes, so a page can refetch what the run
   * wrote. A counter rather than a boolean: two runs in one session have to be
   * two distinct effects.
   */
  finished: number;
  /**
   * What the operator asked for while a run was going, in the order asked. It
   * starts as ONE run, every queued slug together, the moment the current run
   * finishes. Held here, not in the extension, so it is lost with the tab.
   */
  queued: string[];
  /** Add to the queue; a slug already queued is not added twice. */
  enqueue(slugs: string[]): void;
  clearQueue(): void;
  /** The queue's start was refused, so it is still queued; cleared on the next start. */
  queueFailed: boolean;
  /**
   * The run going was started from this tab as Run all, which reads every
   * program and offers page: nothing is left to queue behind it. Unknown for a
   * run started elsewhere, which then counts as an individual run.
   */
  runAllLive: boolean;
  /**
   * The runs since the operator last started one from idle, oldest first: a run
   * the queue started joins the batch, so Refresh lists the changes of all of
   * them. Empty until a run finishes in this tab.
   */
  batch: string[];
  start(slugs?: string[]): Promise<{ started: true } | { error: FailureCode }>;
  stop(): Promise<void>;
  reload(): Promise<{ reloading: true } | { error: FailureCode }>;
  recheck(): void;
  retry(): void;
}

/**
 * What a component sees with no provider above it.
 *
 * `absent` and inert, never null, which is the whole reason this is the default:
 * Points and Offers read `finished` to know when to refetch, and they are
 * rendered on their own in their own tests. A hook that threw would make every
 * one of those tests import a provider to test something unrelated to capture.
 */
export const NO_CAPTURE: CaptureValue = {
  connection: "absent",
  hello: null,
  catalog: null,
  snapshot: null,
  speaks: null,
  catalogFailed: false,
  finished: 0,
  queued: [],
  enqueue: () => undefined,
  clearQueue: () => undefined,
  queueFailed: false,
  runAllLive: false,
  batch: [],
  start: async () => ({ error: "failed" }),
  stop: async () => undefined,
  reload: async () => ({ error: "failed" }),
  recheck: () => undefined,
  retry: () => undefined,
};

/**
 * What the PROVIDER starts from, before its bridge has answered anything.
 *
 * Distinct from NO_CAPTURE, and the distinction is load-bearing. NO_CAPTURE says
 * "there is no provider above me", which is `absent` -- a settled verdict that
 * `/refresh` acts on by redirecting. A provider that seeds from it CLAIMS that
 * verdict on its first render, before it has asked anything, so a direct load of
 * /refresh redirected to Points every time and the handshake landed ~600ms later
 * on a page nobody was on. Found on the first live test (2026-10-04); the page
 * and the provider were each tested alone, and only their seam was wrong.
 *
 * `connecting` is what the bridge itself starts at, so this just stops the
 * provider from overriding it with a different module's default.
 */
export const CONNECTING: CaptureValue = { ...NO_CAPTURE, connection: "connecting" };

export const CaptureContext = createContext<CaptureValue>(NO_CAPTURE);

export function useCapture(): CaptureValue {
  return useContext(CaptureContext);
}

/**
 * Whether Wallet should offer capture controls at all.
 *
 * The one predicate behind the header control, the nav and the route, so the
 * three cannot disagree -- a control that appears while the page it opens
 * redirects away is worse than no control. `reconnecting` counts as present: the
 * extension answered once, so hiding the control while a worker wakes up would
 * make it flicker through every idle period.
 *
 * `outdated` counts too, deliberately. It is the one unavailable state worth
 * showing, because it is actionable and otherwise invisible: the extension is
 * loaded and needs rebuilding, and a hidden control would read as "it is gone".
 */
export function hasCapture(state: Pick<CaptureValue, "connection">): boolean {
  return state.connection === "ready"
    || state.connection === "reconnecting"
    || state.connection === "outdated";
}

/** Whether a run is going right now, for the header's dot and the disabled rows. */
export function isRunning(state: Pick<CaptureValue, "snapshot">): boolean {
  return state.snapshot?.live === true;
}

/** The finished run on screen, or null while one is going or none has run. */
export function finishedRun(state: Pick<CaptureValue, "snapshot">) {
  return state.snapshot?.live ? null : state.snapshot?.last_run ?? null;
}

/**
 * The runs whose changes Refresh lists: the batch the last run closed, or that
 * run alone when this tab did not see the batch (a reload, another tab).
 */
export function batchRuns(state: Pick<CaptureValue, "snapshot" | "batch">): string[] {
  const run = finishedRun(state);
  if (!run) return [];
  return state.batch.includes(run.run_id) ? state.batch : [run.run_id];
}

/** Queue slugs, keeping the order first asked and never one twice. */
export function addToQueue(queued: string[], slugs: string[]): string[] {
  return [...new Set([...queued, ...slugs])];
}
